const YOUTUBE_HOSTS = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtu.be', 'www.youtu.be', 'youtube-nocookie.com', 'www.youtube-nocookie.com']);
const IMAGE = /\.(?:avif|gif|jpe?g|png|webp|svg|bmp)$/i;
const AUDIO = /\.(?:mp3|m4a|m4b|aac|wav|oga|opus|flac|weba|mp4a|mpga|mp2|spx)$/i;
const VIDEO = /\.(?:mp4|webm|ogg|ogv|mov|m4v)$/i;

// Galaxy-only classification. Delivery URLs are returned byte-for-byte after trim.
export function parseGalaxyVideoUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return { kind: 'empty', url: '' };
  let url = value.trim();
  if (/[<>\s\\]/.test(url) || [...url].some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127))
    return { kind: 'invalid', url };
  if (/^(?:(?:www|m)\.)?(?:youtube\.com|youtu\.be)\//i.test(url)) url = `https://${url}`;
  let parsed;
  try { parsed = new URL(url); } catch (_) { return { kind: 'invalid', url }; }
  if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password)
    return { kind: 'invalid', url };
  const host = parsed.hostname.toLowerCase();
  if (YOUTUBE_HOSTS.has(host)) {
    if (parsed.port) return { kind: 'invalid', url };
    const parts = parsed.pathname.split('/').filter(Boolean);
    const id = host.endsWith('youtu.be') ? (parts.length === 1 && parts[0])
      : parts[0] === 'watch' && parts.length === 1 ? parsed.searchParams.get('v')
      : ['live', 'embed', 'shorts', 'v'].includes(parts[0]) && parts.length === 2 ? parts[1] : '';
    if (!/^[\w-]{11}$/.test(id || '')) return { kind: 'invalid', url };
    const normalized = new URL(`https://www.youtube.com/watch?v=${id}`);
    for (const key of ['t', 'start', 'end']) {
      if (parsed.searchParams.has(key)) normalized.searchParams.set(key, parsed.searchParams.get(key));
    }
    normalized.hash = parsed.hash;
    return { kind: 'youtube', url: normalized.toString(), videoId: id };
  }
  const cloudinary = host === 'res.cloudinary.com';
  const path = parsed.pathname;
  if (IMAGE.test(path) || (cloudinary && /\/image\/(?:upload|fetch|private|authenticated)\//.test(path)))
    return { kind: 'image', url };
  if (parsed.protocol !== 'https:' || AUDIO.test(path) || /\.(?:html?|pdf|json|txt|zip)$/i.test(path) ||
      (cloudinary && /(?:\/|,)f_(?:mp3|aac|wav|flac|ogg)(?:,|\/)/i.test(path)))
    return { kind: 'invalid', url };
  if (/\.m3u8$/i.test(path)) return { kind: 'hls', url };
  if (/\.mpd$/i.test(path)) return { kind: 'dash', url };
  if (VIDEO.test(path)) return { kind: 'file', url };
  if (cloudinary && /\/video\/(?:upload|fetch|private|authenticated)\/.+/.test(path))
    return { kind: 'file', url };
  return { kind: 'invalid', url };
}

export const isGalaxyVideo = (media) => ['youtube', 'file', 'hls', 'dash'].includes(media?.kind);
export const publicationRevision = (galaxy) => Number.isSafeInteger(galaxy?.coverMediaPublication?.revision)
  && galaxy.coverMediaPublication.revision >= 0 ? galaxy.coverMediaPublication.revision : 0;

export function galaxyVideoPermissions(galaxy, player) {
  const actor = player?.data?.player;
  const id = actor?._id;
  const includes = (list) => (list || []).some((item) => String(item?._id || item?.id || item) === String(id));
  const member = Boolean(id && includes(galaxy?.members) && !includes(galaxy?.excludedMemberIds));
  const assigned = ['owners', 'administrators', 'moderators'].some((key) => includes(galaxy?.[key]));
  const save = Boolean(galaxy?._id && player?.token && actor?.active !== false && ['admin', 'galaxyAdmin'].includes(actor?.role));
  return { view: member && actor?.active !== false, save, live: save && member && assigned, upload: save && assigned };
}
